import { useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import CachedIcon from '@mui/icons-material/Cached';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import BiotechIcon from '@mui/icons-material/Biotech';
import { useConfirm } from 'material-ui-confirm';
import { useApiContext } from '@/contexts/apiContext';
import { useNotificationContext } from '@/contexts/notificationContext';
import { useUserContext } from '@/contexts/userContext';

interface TenantOption {
  id: string;
  name?: string;
}

const AdminActions = () => {
  const theme = useTheme();
  const confirm = useConfirm();
  const { clearCache, syncEntityEmbeddings, syncBiopharmReferenceData, getAllTenants } = useApiContext();
  const { toast } = useNotificationContext();
  const userContext = useUserContext();
  const [clearingCache, setClearingCache] = useState(false);
  const [syncingEmbeddings, setSyncingEmbeddings] = useState(false);
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [referenceTenantId, setReferenceTenantId] = useState<string>(userContext.user?.tenantId || '');
  const [syncingReferenceData, setSyncingReferenceData] = useState(false);

  useEffect(() => {
    getAllTenants()
      .then((data) => setTenants(Array.isArray(data) ? data : []))
      .catch(() => setTenants([]));
  }, []);

  const tenantOptions: TenantOption[] =
    referenceTenantId && !tenants.some((t) => t.id === referenceTenantId)
      ? [{ id: referenceTenantId }, ...tenants]
      : tenants;

  const handleClearCache = async () => {
    try {
      await confirm({
        title: 'Clear Definition Cache',
        description:
          'Are you sure you want to clear the entity definition cache? The cache will be repopulated on demand.',
        confirmationText: 'Clear Cache',
        cancellationText: 'Cancel',
      });
    } catch {
      return;
    }

    try {
      setClearingCache(true);
      await clearCache();
      toast({ message: 'Definition cache cleared successfully.', type: 'success' });
    } catch (error: any) {
      toast({ message: 'Failed to clear cache: ' + (error?.message || 'Unknown error'), type: 'error' });
    } finally {
      setClearingCache(false);
    }
  };

  const handleSyncEmbeddings = async () => {
    try {
      await confirm({
        title: 'Sync Entity Vector Embeddings',
        description:
          'Are you sure you want to trigger a bulk sync of vector embeddings for all entities and pipelines in this tenant? This will recompute and store embeddings using pgvector.',
        confirmationText: 'Sync Embeddings',
        cancellationText: 'Cancel',
      });
    } catch {
      return;
    }

    try {
      setSyncingEmbeddings(true);
      const res = await syncEntityEmbeddings();
      toast({
        message: `Embeddings sync completed: ${res.indexed} indexed, ${res.failed} failed.`,
        type: res.failed > 0 ? 'warning' : 'success',
      });
    } catch (error: any) {
      toast({
        message: 'Failed to sync embeddings: ' + (error?.message || 'Unknown error'),
        type: 'error',
      });
    } finally {
      setSyncingEmbeddings(false);
    }
  };

  const handleSyncReferenceData = async () => {
    if (!referenceTenantId) return;
    try {
      await confirm({
        title: 'Sync BioPharm Reference Data',
        description: `Load the current Open Targets and DISEASES reference releases for tenant "${referenceTenantId}"? The tenant must have the BioPharm domain enabled. The load runs in the Reference Data Store Agent and can take about two hours; until it finishes, Target Identification uses live queries for this tenant.`,
        confirmationText: 'Start Sync',
        cancellationText: 'Cancel',
      });
    } catch {
      return;
    }

    try {
      setSyncingReferenceData(true);
      await syncBiopharmReferenceData(referenceTenantId);
      toast({
        message: `Reference data sync queued for ${referenceTenantId}. It continues in the background.`,
        type: 'success',
      });
    } catch (error: any) {
      const detail = error?.response?.data?.message || error?.message || 'Unknown error';
      toast({ message: 'Failed to start the reference data sync: ' + detail, type: 'error' });
    } finally {
      setSyncingReferenceData(false);
    }
  };

  return (
    <Box
      sx={{
        height: '100%',
        overflowY: 'auto',
        pr: { xs: 0, md: 1 },
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
      }}
    >
      {/* Header */}
      <Box sx={{ pb: 1, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
          Admin Actions
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
          Execute system-level maintenance actions, cache operations, and administrative utilities.
        </Typography>
      </Box>

      {/* Action Cards Container */}
      <Stack spacing={2.5}>
        {/* Definition Cache Card */}
        <Card
          variant="outlined"
          sx={{
            borderRadius: '16px',
            borderColor: 'divider',
            bgcolor: alpha(theme.palette.background.paper, 0.6),
            backdropFilter: 'blur(8px)',
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: alpha(theme.palette.secondary.main, 0.4),
              boxShadow: theme.shadows[1],
            },
          }}
        >
          <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2.5}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: alpha(theme.palette.secondary.main, 0.1),
                    color: theme.palette.secondary.main,
                    flexShrink: 0,
                  }}
                >
                  <CachedIcon sx={{ fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    Entity Definition Cache
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5, maxWidth: 650 }}>
                    Clears in-memory caches for Agent Definitions, Data Source Definitions, User App Definitions, and
                    Pipeline Blueprints across the tenant. Cached items will be re-fetched from the database on demand.
                  </Typography>
                </Box>
              </Stack>

              <Button
                variant="outlined"
                color="primary"
                startIcon={clearingCache ? <CircularProgress size={16} /> : <CachedIcon />}
                disabled={clearingCache}
                onClick={handleClearCache}
                sx={{
                  borderRadius: '12px',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  px: 2.5,
                  py: 1,
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  alignSelf: { xs: 'stretch', sm: 'center' },
                  bgcolor: alpha(theme.palette.background.paper, 0.5),
                  '&:hover': {
                    bgcolor: alpha(theme.palette.secondary.main, 0.08),
                  },
                }}
              >
                Clear Cache
              </Button>
            </Stack>
          </CardContent>
        </Card>

        {/* Vector Embeddings Bulk Sync Card */}
        <Card
          variant="outlined"
          sx={{
            borderRadius: '16px',
            borderColor: 'divider',
            bgcolor: alpha(theme.palette.background.paper, 0.6),
            backdropFilter: 'blur(8px)',
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: alpha(theme.palette.primary.main, 0.4),
              boxShadow: theme.shadows[1],
            },
          }}
        >
          <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2.5}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    flexShrink: 0,
                  }}
                >
                  <AutoAwesomeIcon sx={{ fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    Entity Vector Embeddings Sync
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5, maxWidth: 650 }}>
                    Bulk synchronizes pgvector vector embeddings for all Agent Definitions &amp; Instances, Data Source
                    Definitions &amp; Instances, User App Definitions &amp; Instances, and Pipelines across the tenant.
                    Extracts names, descriptions, and metadata to power semantic similarity search.
                  </Typography>
                </Box>
              </Stack>

              <Button
                variant="contained"
                color="primary"
                startIcon={syncingEmbeddings ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
                disabled={syncingEmbeddings}
                onClick={handleSyncEmbeddings}
                sx={{
                  borderRadius: '12px',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  px: 2.5,
                  py: 1,
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  alignSelf: { xs: 'stretch', sm: 'center' },
                }}
              >
                {syncingEmbeddings ? 'Syncing...' : 'Sync Embeddings'}
              </Button>
            </Stack>
          </CardContent>
        </Card>

        {/* BioPharm Reference Data Sync Card */}
        <Card
          variant="outlined"
          sx={{
            borderRadius: '16px',
            borderColor: 'divider',
            bgcolor: alpha(theme.palette.background.paper, 0.6),
            backdropFilter: 'blur(8px)',
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: alpha(theme.palette.success.main, 0.4),
              boxShadow: theme.shadows[1],
            },
          }}
        >
          <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2.5}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                    color: theme.palette.success.main,
                    flexShrink: 0,
                  }}
                >
                  <BiotechIcon sx={{ fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    BioPharm Reference Data Sync
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5, maxWidth: 650 }}>
                    Loads the current Open Targets and Jensen Lab DISEASES releases for a tenant that has just enabled
                    the BioPharm domain, so Target Identification can rank targets from local data. Runs in the
                    background; asking again resumes an interrupted load.
                  </Typography>
                  <Autocomplete
                    size="small"
                    options={tenantOptions}
                    value={tenantOptions.find((t) => t.id === referenceTenantId) || null}
                    onChange={(_, option) => setReferenceTenantId(option?.id || '')}
                    getOptionLabel={(option) =>
                      option.name && option.name !== option.id ? `${option.name} (${option.id})` : option.id
                    }
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    sx={{ mt: 2, maxWidth: 360 }}
                    renderInput={(params) => <TextField {...params} label="Tenant" />}
                  />
                </Box>
              </Stack>

              <Button
                variant="outlined"
                color="primary"
                startIcon={syncingReferenceData ? <CircularProgress size={16} /> : <BiotechIcon />}
                disabled={syncingReferenceData || !referenceTenantId}
                onClick={handleSyncReferenceData}
                sx={{
                  borderRadius: '12px',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  px: 2.5,
                  py: 1,
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  alignSelf: { xs: 'stretch', sm: 'center' },
                  bgcolor: alpha(theme.palette.background.paper, 0.5),
                  '&:hover': {
                    bgcolor: alpha(theme.palette.success.main, 0.08),
                  },
                }}
              >
                {syncingReferenceData ? 'Starting...' : 'Sync Reference Data'}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
};

export default AdminActions;
